import { Alert, AlertTitle, Box, Container, Typography } from '@mui/material'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { finalizeSubscriptionAfterPayment, subscribeExistingUser } from 'api/subscription'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import { STRIPE_KEY } from 'const'
import {
  hasActiveEntitlement,
  isPaymentRecovery,
  isSubscriptionGated,
} from 'features/Subscription/utils'
import { formatPlanPeriod, formatPlanPrice, usePlanConfig } from 'hooks/usePublicConfig'
import { useCallback, useMemo } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { SubscribeResponse, TRAINEE_PLAN } from 'types'
import { AddSubscriptionHeader } from './AddSubscriptionHeader'
import { ExpiredSubscriptionNotice } from './ExpiredSubscriptionNotice'
import { SubscriptionPaymentForm } from './SubscriptionPaymentForm'

function AddSubscriptionForm() {
  const navigate = useNavigate()
  const { refreshUser, isIndividualTrainee } = useAuthContext()
  const traineePlan = usePlanConfig(TRAINEE_PLAN)

  const handleSuccess = useCallback(
    async (response: SubscribeResponse) => {
      await finalizeSubscriptionAfterPayment(response)
      await refreshUser()
      navigate(routes.journey)
    },
    [navigate, refreshUser],
  )

  const handlePaymentFailed = useCallback((errorMessage?: string) => {
    NotificationSys.showWarning(
      errorMessage || 'Payment could not be processed. Please try a different card.',
    )
  }, [])

  return (
    <SubscriptionPaymentForm
      title={
        <Typography variant="h5" fontWeight={600} textAlign="center">
          Complete Your Subscription
        </Typography>
      }
      submitLabel="Subscribe"
      // An individual trainee has a single plan, so there is nothing to choose.
      fixedPlan={isIndividualTrainee ? TRAINEE_PLAN : undefined}
      description={
        isIndividualTrainee ? (
          <Typography variant="body2" color="text.secondary">
            Your subscription provides full access to MemoryCarePartner Pro Training
            {traineePlan &&
              ` for ${formatPlanPrice(traineePlan)} per ${formatPlanPeriod(traineePlan)}`}
            . It renews automatically until cancelled.
          </Typography>
        ) : undefined
      }
      onSubmit={subscribeExistingUser}
      onSuccess={handleSuccess}
      onPaymentFailed={handlePaymentFailed}
    />
  )
}

export function AddSubscriptionPage() {
  const { checkIsLoggedIn, user } = useAuthContext()
  const stripePromise = useMemo(() => loadStripe(STRIPE_KEY), [])

  if (!checkIsLoggedIn()) {
    return <Navigate to={routes.signIn} replace />
  }

  // Everyone whose access does not depend on a subscription — including organization-based
  // trainees — has nothing to do here.
  if (!isSubscriptionGated(user)) {
    return <Navigate to={routes.journey} replace />
  }

  if (hasActiveEntitlement(user) || isPaymentRecovery(user)) {
    return <Navigate to={routes.journey} replace />
  }

  return (
    <>
      <AddSubscriptionHeader />

      <Container
        component="main"
        sx={{ display: 'flex', justifyContent: 'center', marginTop: '4rem' }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="100%"
          sx={{ maxWidth: 460, mt: 6 }}
        >
          <Box sx={{ width: '100%', background: 'white', p: 4 }}>
            {/* Someone who never subscribed sees the plain form; a lapsed subscriber sees why. */}
            {user?.familyAccessEnded ? (
              <Box mb={3}>
                <Alert severity="warning">
                  <AlertTitle>Your Family Subscription has ended</AlertTitle>
                  The Family Subscription you used has ended. Choose your own plan below.
                </Alert>
              </Box>
            ) : (
              user?.lastSubscription && (
                <Box mb={3}>
                  <ExpiredSubscriptionNotice subscription={user.lastSubscription} />
                </Box>
              )
            )}
            <Elements stripe={stripePromise}>
              <AddSubscriptionForm />
            </Elements>
          </Box>
        </Box>
      </Container>
    </>
  )
}
